/**
 * Universal Cast control for top nav — app-level persistent Cast session.
 * Session is owned by AppCastingSession / CastingManager, not VideoPlayer.
 * Opening/closing a video does not start or terminate Cast.
 *
 * Menu structure:
 *   Cast → (DEVICES when discoverable) → Screen Mirror action
 * Empty: "No cast devices found" + Screen Mirror only.
 * Never lists unavailable platforms as stub rows.
 * Cast to Device ≠ Screen Mirror (OS external-display handoff).
 * Pair Roku is not shown in this menu (cloud API remains; Roku only if discoverable).
 */

import React, { Component } from "react";
import {
  ensureAppCastingSession,
  getAppCastingSession,
} from "../../../video-player/Providers/Casting/AppCastingSession";
import { CastingUiState } from "../../../video-player/Providers/Casting/CastingEvents";
import { isCastHeaderEnabled } from "../../Helper/siteContent";

var PROVIDER_LABELS = {
  chromecast: "Chromecast",
  airplay: "AirPlay",
  roku: "Roku",
  firetv: "Amazon Fire TV",
  samsung: "Samsung TV",
  lg: "LG TV",
  future_native: "Native App",
  simulated: "Simulated",
};

function titleForState(uiState, sessionInfo) {
  var device =
    (sessionInfo && sessionInfo.deviceName) ||
    (sessionInfo && sessionInfo.device && sessionInfo.device.name) ||
    null;
  switch (uiState) {
    case CastingUiState.CASTING:
      return device ? "Casting to " + device : "Casting to TV";
    case CastingUiState.CONNECTED:
      return device
        ? "Connected to " + device + " (idle)"
        : "Connected — waiting for video";
    case CastingUiState.CONNECTING:
      return "Connecting to Cast device…";
    case CastingUiState.AVAILABLE:
    case CastingUiState.DISCONNECTED:
      // Empty-device copy lives once in the menu body — do not duplicate here.
      return "Cast to TV";
    case CastingUiState.ERROR:
      return "Cast error — try again";
    case CastingUiState.UNAVAILABLE:
    default:
      return "Casting not available on this device";
  }
}

function sectionLabelStyle() {
  return {
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    opacity: 0.7,
    margin: "0.55rem 0 0.35rem",
  };
}

export default class UniversalCastButton extends Component {
  constructor(props) {
    super(props);
    this.state = {
      uiState: CastingUiState.UNAVAILABLE,
      sessionInfo: null,
      groups: [],
      open: false,
      visible: isCastHeaderEnabled(),
      screenMirror: null,
      screenMirrorHint: null,
    };
    this._unsubs = [];
  }

  componentDidMount() {
    if (!this.state.visible) {
      return;
    }
    ensureAppCastingSession();
    this.refresh();
    this._poll = setInterval(() => this.refresh(), 2500);
    if (typeof window !== "undefined") {
      window.addEventListener("tpg-casting-changed", this.refresh);
    }
  }

  componentWillUnmount() {
    if (this._poll) clearInterval(this._poll);
    if (typeof window !== "undefined") {
      window.removeEventListener("tpg-casting-changed", this.refresh);
    }
    this._unsubs.forEach((u) => {
      try {
        u();
      } catch (e) {
        /* ignore */
      }
    });
  }

  refresh = () => {
    if (!isCastHeaderEnabled()) {
      if (this.state.visible) {
        this.setState({ visible: false });
      }
      return;
    }
    const session = getAppCastingSession() || ensureAppCastingSession();
    const manager = session && session.getManager && session.getManager();
    if (!manager) {
      this.setState({
        visible: true,
        uiState: CastingUiState.UNAVAILABLE,
        sessionInfo: null,
        groups: [],
        screenMirror: null,
      });
      return;
    }
    const uiState =
      (manager.getUiState && manager.getUiState()) ||
      CastingUiState.UNAVAILABLE;
    const sessionInfo =
      (manager.getSessionInfo && manager.getSessionInfo()) || null;
    // Prefer discoverable-only groups so stubs never reach the menu.
    const groups =
      (manager.getDiscoverableDeviceGroups &&
        manager.getDiscoverableDeviceGroups()) ||
      [];
    const screenMirror =
      (manager.getScreenMirrorSnapshot && manager.getScreenMirrorSnapshot()) ||
      (session.getScreenMirrorSnapshot && session.getScreenMirrorSnapshot()) ||
      null;
    this.setState({
      visible: true,
      uiState,
      sessionInfo,
      groups: groups,
      screenMirror: screenMirror,
    });
  };

  toggleMenu = (e) => {
    e.preventDefault();
    this.setState((s) => ({ open: !s.open, screenMirrorHint: null }));
  };

  startCast = (e, providerName, deviceId) => {
    e.preventDefault();
    const session = getAppCastingSession() || ensureAppCastingSession();
    if (!session || !session.start) {
      this.setState({
        uiState: CastingUiState.UNAVAILABLE,
        open: true,
      });
      return;
    }
    this.setState({ uiState: CastingUiState.CONNECTING, open: true });
    var startPromise = providerName
      ? session.start(providerName, deviceId)
      : session.start();
    startPromise
      .then(() => this.refresh())
      .catch(() => {
        this.setState({ uiState: CastingUiState.ERROR, open: true });
      });
  };

  applyScreenMirrorResult = (result) => {
    var hint = null;
    if (result && result.launched === false && result.fallbackHint) {
      hint = result.fallbackHint;
    }
    this.setState({
      open: true,
      screenMirrorHint: hint,
    });
    this.refresh();
  };

  startScreenMirror = (e) => {
    e.preventDefault();
    const session = getAppCastingSession() || ensureAppCastingSession();
    if (!session || !session.startScreenMirror) {
      this.setState({
        open: true,
        screenMirrorHint: "Use your device's built-in screen mirroring controls.",
      });
      return;
    }
    session
      .startScreenMirror()
      .then((result) => this.applyScreenMirrorResult(result))
      .catch(() => {
        this.setState({
          open: true,
          screenMirrorHint:
            "Use your device's built-in screen mirroring controls.",
        });
      });
  };

  stopScreenMirror = (e) => {
    e.preventDefault();
    const session = getAppCastingSession() || ensureAppCastingSession();
    if (!session || !session.stopScreenMirror) {
      this.setState({
        open: true,
        screenMirrorHint: "Use your device's built-in screen mirroring controls.",
      });
      return;
    }
    session
      .stopScreenMirror()
      .then((result) => this.applyScreenMirrorResult(result))
      .catch(() => {
        this.setState({
          open: true,
          screenMirrorHint:
            "Use your device's built-in screen mirroring controls.",
        });
      });
  };

  stopCast = (e) => {
    e.preventDefault();
    const session = getAppCastingSession();
    if (!session || !session.stop) return;
    session
      .stop()
      .then(() => this.refresh())
      .catch(() => {});
  };

  render() {
    if (!this.state.visible) {
      return null;
    }

    const { uiState, sessionInfo, open, groups, screenMirror, screenMirrorHint } =
      this.state;
    const remote =
      uiState === CastingUiState.CASTING ||
      uiState === CastingUiState.CONNECTED ||
      uiState === CastingUiState.CONNECTING;
    const canStart =
      uiState === CastingUiState.AVAILABLE ||
      uiState === CastingUiState.DISCONNECTED ||
      uiState === CastingUiState.ERROR;
    const label =
      uiState === CastingUiState.CASTING || uiState === CastingUiState.CONNECTED
        ? "Casting"
        : "Cast";
    const title = titleForState(uiState, sessionInfo);
    const deviceName =
      (sessionInfo && sessionInfo.deviceName) ||
      (sessionInfo && sessionInfo.device && sessionInfo.device.name) ||
      null;
    const discoverable = groups || [];
    const mirrorActive = !!(screenMirror && screenMirror.active);
    const emptyDevices = !remote && discoverable.length === 0;
    // Idle empty: one "No cast devices found" heading — skip redundant status line.
    const showMenuStatus =
      !emptyDevices ||
      (uiState !== CastingUiState.AVAILABLE &&
        uiState !== CastingUiState.DISCONNECTED);

    // Font Awesome 5.0.x (site CDN + bundled) has no fa-cast glyph — empty
    // <i> made the control invisible despite mounting. Material Icons is already
    // loaded site-wide and includes the standard cast / cast_connected ligatures.
    const self = this;
    return (
      <li
        className="nav-item gift tpg-universal-cast"
        title={title}
        data-cast-state={uiState}
        style={{ position: "relative" }}
      >
        <button
          type="button"
          className={
            "nav-link btn btn-link tpg-cast-btn" +
            (remote ? " is-casting" : "") +
            (uiState === CastingUiState.UNAVAILABLE ? " is-disabled" : "")
          }
          aria-label={label}
          onClick={this.toggleMenu}
          style={{
            background: "transparent",
            border: "none",
            color: "inherit",
            padding: "0.35rem 0.6rem",
            opacity: uiState === CastingUiState.UNAVAILABLE ? 0.55 : 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "2.4em",
            minWidth: "2.4em",
          }}
        >
          <i
            className="material-icons tpg-cast-icon"
            aria-hidden="true"
            style={{
              fontSize: "1.7em",
              lineHeight: 1,
              color: "var(--header-icon, #ffffff)",
              verticalAlign: "middle",
            }}
          >
            {remote ? "cast_connected" : "cast"}
          </i>
          <span className="sr-only">{label}</span>
        </button>
        {open ? (
          <div
            className="tpg-cast-menu"
            style={{
              position: "absolute",
              right: 0,
              minWidth: 260,
              maxWidth: "min(340px, calc(100vw - 24px))",
              width: "max-content",
              boxSizing: "border-box",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              zIndex: 1050,
              background: "#111",
              color: "#fff",
              padding: "0.75rem",
              borderRadius: 4,
              boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
            }}
          >
            {showMenuStatus ? (
              <p style={{ margin: "0 0 0.35rem", fontSize: "0.85rem" }}>
                {title}
              </p>
            ) : null}
            {discoverable.length > 0 ? (
              <p
                style={{
                  margin: "0 0 0.5rem",
                  fontSize: "0.72rem",
                  opacity: 0.75,
                }}
              >
                Cast to Device sends playback to a discovered receiver. Screen
                Mirror uses your device's built-in mirroring.
              </p>
            ) : null}
            {deviceName && remote ? (
              <p
                style={{
                  margin: "0 0 0.5rem",
                  fontSize: "0.8rem",
                  opacity: 0.85,
                }}
              >
                {deviceName}
              </p>
            ) : null}
            {remote &&
            sessionInfo &&
            sessionInfo.pairingCode &&
            sessionInfo.provider === "roku" ? (
              <div
                style={{
                  margin: "0 0 0.65rem",
                  padding: "0.5rem",
                  background: "#1a2433",
                  borderRadius: 4,
                }}
              >
                <div
                  style={{
                    fontSize: "0.72rem",
                    opacity: 0.75,
                    marginBottom: 4,
                  }}
                >
                  Enter this code on the Pentecost+ Cast channel
                </div>
                <div
                  style={{
                    fontSize: "1.35rem",
                    letterSpacing: "0.2em",
                    fontWeight: 700,
                  }}
                >
                  {sessionInfo.pairingCode}
                </div>
                <div
                  style={{
                    fontSize: "0.68rem",
                    opacity: 0.65,
                    marginTop: 6,
                  }}
                >
                  Sideload required · no fake device discovery
                </div>
              </div>
            ) : null}

            {!remote ? (
              <div className="tpg-cast-groups">
                {discoverable.length === 0 ? (
                  <p
                    style={{
                      margin: "0 0 0.5rem",
                      fontSize: "0.8rem",
                      opacity: 0.85,
                    }}
                  >
                    No cast devices found
                  </p>
                ) : (
                  <div className="tpg-cast-section-devices">
                    <div style={sectionLabelStyle()}>Devices</div>
                    {discoverable.map((g) => (
                      <div key={g.provider} style={{ marginBottom: "0.45rem" }}>
                        <div
                          style={{
                            fontSize: "0.72rem",
                            opacity: 0.7,
                            marginBottom: 2,
                          }}
                        >
                          {PROVIDER_LABELS[g.provider] || g.provider}
                        </div>
                        {(g.devices || []).map((d) => (
                          <button
                            key={d.id}
                            type="button"
                            className="btn btn-sm btn-primary"
                            style={{
                              display: "block",
                              width: "100%",
                              marginBottom: 4,
                              textAlign: "left",
                              whiteSpace: "normal",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                              lineHeight: 1.35,
                              height: "auto",
                              minHeight: "2rem",
                            }}
                            onClick={(ev) =>
                              self.startCast(ev, g.provider, d.id)
                            }
                          >
                            {d.name}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  className={
                    "btn btn-sm tpg-screen-mirror-btn " +
                    (mirrorActive ? "btn-light" : "btn-secondary")
                  }
                  style={{
                    display: "block",
                    width: "100%",
                    marginBottom: 4,
                    textAlign: "left",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    lineHeight: 1.35,
                    height: "auto",
                    minHeight: "2rem",
                  }}
                  onClick={
                    mirrorActive
                      ? self.stopScreenMirror
                      : self.startScreenMirror
                  }
                >
                  {mirrorActive ? "Stop Screen Mirror" : "Screen Mirror"}
                </button>
                {screenMirrorHint ? (
                  <p
                    className="tpg-screen-mirror-hint"
                    style={{
                      margin: "0 0 0.35rem",
                      fontSize: "0.72rem",
                      opacity: 0.8,
                      lineHeight: 1.35,
                    }}
                  >
                    {screenMirrorHint}
                  </p>
                ) : null}
              </div>
            ) : null}

            {remote ? (
              <button
                type="button"
                className="btn btn-sm btn-light"
                style={{
                  display: "block",
                  width: "100%",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  height: "auto",
                  minHeight: "2rem",
                }}
                onClick={this.stopCast}
              >
                Stop casting
              </button>
            ) : discoverable.length > 0 ? (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={(e) => this.startCast(e)}
                disabled={
                  uiState === CastingUiState.UNAVAILABLE ||
                  uiState === CastingUiState.CONNECTING ||
                  !canStart
                }
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: "0.35rem",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  height: "auto",
                  minHeight: "2rem",
                }}
              >
                {canStart ? "Cast to device" : "Unavailable"}
              </button>
            ) : null}
          </div>
        ) : null}
      </li>
    );
  }
}
